export const QUERY_KEYS = {
    SUMMARY: (month?: number, year?: number) =>
        ['credit-consumption', 'summary', month ?? 0, year ?? 0] as const,
    TRANSACTIONS: (page: number, limit: number, includeZeroCredit?: boolean) =>
        ['credit-consumption', 'transactions', page, limit, includeZeroCredit ?? false] as const,
    USERS: (month?: number, year?: number) =>
        ['credit-consumption', 'users', month ?? 0, year ?? 0] as const,
    FLEET: (planTier?: string, orgName?: string, month?: number, year?: number) =>
        [
            'credit-consumption',
            'fleet',
            planTier ?? '',
            orgName ?? '',
            month ?? 0,
            year ?? 0,
        ] as const,
    ORG_DETAIL: (orgId: string, month?: number, year?: number) =>
        ['credit-consumption', 'org', orgId, month ?? 0, year ?? 0] as const,
    ORG_USERS: (orgId: string, month?: number, year?: number) =>
        ['credit-consumption', 'org', orgId, 'users', month ?? 0, year ?? 0] as const,
    ORG_TRANSACTIONS: (
        orgId: string,
        page: number,
        limit: number,
        includeGrants?: boolean,
        includeZeroCredit?: boolean,
    ) =>
        [
            'credit-consumption',
            'org',
            orgId,
            'transactions',
            page,
            limit,
            includeGrants ?? false,
            includeZeroCredit ?? false,
        ] as const,
    RATE_CARD: ['credit-consumption', 'rates'] as const,
    /** CS actions (M4S-5911, plan step 23). */
    ORG_BALANCE: (orgId: string) => ['credit-consumption', 'org', orgId, 'balance'] as const,
    ORG_ADJUSTMENTS: (orgId: string) =>
        ['credit-consumption', 'org', orgId, 'adjustments'] as const,
    ADJUSTMENT_PERMISSIONS: ['credit-consumption', 'adjustment-permissions'] as const,
    ADJUSTMENT_REASONS: ['credit-consumption', 'adjustment-reasons'] as const,
} as const;

export const DISPLAY_MODES = {
    CREDITS: 'credits' as const,
    DOLLARS: 'dollars' as const,
};

/** Ledger U+2212 — shared by remaining displays and debit prefixes. */
export const LEDGER_MINUS = '\u2212';

/** Shared negative-remaining colour. Do not invent a per-surface danger token. */
export const NEGATIVE_BALANCE_CLASS = 'text-danger-600';

/** State-neutral annotation until OQ-13 names a commercial phrase. */
export const NEGATIVE_BALANCE_ANNOTATION = 'Below zero';

export const ALERT_THRESHOLDS = {
    /** Fallback when warnThresholdPct is unavailable. Live banners must not read this as the trigger. */
    DANI_WARNING: 0.8,
    /** Hard pause at 100% of monthly cap — separate design constant, not warnThresholdPct. */
    DANI_DANGER: 1.0,
    /** Annual-pool amber at 90% — separate design constant, not warnThresholdPct. */
    POOL_WARNING: 0.9,
} as const;

/**
 * Live R-TRANS-1 low-balance trigger. Uses the configured `warnThresholdPct`
 * from `credit_global_settings` when present; falls back to
 * `ALERT_THRESHOLDS.DANI_WARNING` (0.8) when the settings/summary fetch fails
 * or the value is unusable. Do not invent a threshold-derived credit figure
 * on the fallback path — only the comparison uses this number.
 */
export function resolveWarnThresholdPct(value: number | null | undefined): number {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 1) {
        return value;
    }
    return ALERT_THRESHOLDS.DANI_WARNING;
}

export const LEDGER_PAGE_SIZE = 50;

export const FEATURE_FLAG_KEY = 'credit-consumption';

export const API_ENDPOINTS = {
    SUMMARY: '/credit-consumption',
    TRANSACTIONS: '/credit-consumption/transactions',
    USERS: '/credit-consumption/users',
    SPEND_CHECK: '/credit-consumption/spend-check',
    FLEET: '/admin/credit-consumption',
    GLOBAL_SETTINGS: '/admin/credit-consumption/global-settings',
    /** M4S-6013: the AI ratio beside the Snowflake ratio. */
    AI_RATIO: '/admin/credit-consumption/ai-ratio',
    ORG_DETAIL: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption`,
    ORG_USERS: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/users`,
    ORG_TRANSACTIONS: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/transactions`,
    OVERRIDE: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/override`,
    REVOKE_OVERRIDE: (orgId: string, overrideId: string) =>
        `/admin/orgs/${orgId}/credit-consumption/override/${overrideId}`,
    TOPOFFS: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/topoffs`,
    REVOKE_TOPOFF: (orgId: string, topoffId: string) =>
        `/admin/orgs/${orgId}/credit-consumption/topoffs/${topoffId}`,
    ORG_SETTINGS: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/settings`,
    DANI_CAP: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/dani-cap`,
    USER_SPEND_LIMITS: (orgId: string) =>
        `/admin/orgs/${orgId}/credit-consumption/user-spend-limits`,
    CONTRACT: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/contract`,
    SYNC_SALESFORCE: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/sync/salesforce`,
    PREVIEW_SALESFORCE: (orgId: string) =>
        `/admin/orgs/${orgId}/credit-consumption/preview/salesforce`,
    ORG_SEARCH: '/admin/credit-consumption/orgs/search',
    RATES: '/admin/credit-consumption/rates',
    RATE_CARD: '/credit-consumption/rates',
    MARGINS: '/admin/credit-consumption/margins',
    COGS: '/admin/credit-consumption/cogs',
    COGS_INVOICES: '/admin/credit-consumption/cogs/invoices',
    COSTS: '/admin/credit-consumption/costs',
    COST_VERSIONS: (functionKey: string) => `/admin/credit-consumption/costs/${functionKey}`,
    RATE_VERSIONS: (functionKey: string) => `/admin/credit-consumption/rates/${functionKey}`,
    PRICING_HISTORY: '/admin/credit-consumption/pricing-history',
    LEDGER_SHADOW: '/admin/credit-consumption/ledger/shadow',
    LEDGER_SHADOW_NOTES: '/admin/credit-consumption/ledger/shadow/notes',
    /** One org's `CreditBalance` (§2.4); `reader` says whether it is on the new balance. */
    ORG_BALANCE: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/balance`,
    /** CS actions (M4S-5911, §5). Staff only; the View-as write policy denies every one. */
    ADJUSTMENT_PERMISSIONS: '/admin/credit-consumption/permissions',
    ADJUSTMENT_REASONS: '/admin/credit-consumption/adjustment-reasons',
    ADJUSTMENTS: '/admin/credit-consumption/adjustments',
    ADD_CREDITS: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/adjustments/add`,
    REMOVE_CREDITS: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/adjustments/remove`,
    TRANSFERS: '/admin/credit-consumption/transfers',
    REVERSE_ROW: (rowId: number) => `/admin/credit-consumption/ledger/${rowId}/reverse`,
    EXTEND_GRANT: (orgId: string, rowId: number) =>
        `/admin/orgs/${orgId}/credit-consumption/ledger/${rowId}/extend`,
} as const;

export const QUERY_KEYS_ADMIN = {
    GLOBAL_SETTINGS: ['credit-consumption', 'global-settings'] as const,
    AI_RATIO: ['credit-consumption', 'ai-ratio'] as const,
    RATE_CARD: ['credit-consumption', 'rates'] as const,
    RATES: ['credit-consumption', 'rates'] as const,
    RATE_VERSIONS: (functionKey: string) => ['credit-consumption', 'rates', functionKey] as const,
    PRICING_HISTORY: ['credit-consumption', 'pricing-history'] as const,
    MARGINS: (orgId?: string) => ['credit-consumption', 'margins', orgId ?? 'global'] as const,
    COGS: (start: string, end: string) => ['credit-consumption', 'cogs', start, end] as const,
    COSTS: ['credit-consumption', 'costs'] as const,
    ORG_SETTINGS: (orgId: string) => ['credit-consumption', 'org', orgId, 'settings'] as const,
    LEDGER_SHADOW: (date?: string) =>
        ['credit-consumption', 'ledger-shadow', date ?? 'latest'] as const,
} as const;

/** Week-1 function keys (ADR 5). Validated strings until C-4 owns the registry. */
export const WEEK1_FUNCTION_KEYS = [
    'dani_compute',
    'report_managed',
    'report_self_service',
    'survey_complete',
    'transcription',
    'data_export',
    'crosstab_build',
    'video_processing',
    'pptx_export',
] as const;

export type Week1FunctionKey = (typeof WEEK1_FUNCTION_KEYS)[number];

export const FUNCTION_KEY_META: Record<Week1FunctionKey, { label: string; unit: string }> = {
    dani_compute: { label: 'DANI compute', unit: 'per hour' },
    report_managed: { label: 'Managed report', unit: 'per report' },
    report_self_service: { label: 'Self-service report', unit: 'compound' },
    survey_complete: { label: 'Survey complete', unit: 'per complete' },
    transcription: { label: 'Transcription', unit: 'per audio minute' },
    data_export: { label: 'Data export', unit: 'per export' },
    crosstab_build: { label: 'Crosstab build', unit: 'per build' },
    video_processing: { label: 'Video processing', unit: 'per video minute' },
    pptx_export: { label: 'PPTX export', unit: 'per deck' },
};
